import axios from 'axios';
import { API_URL, isNativeApp } from './config';

let lastEndpoint = null;
let subscribed = false;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function isStandalonePwa() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

export function isIosDevice() {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

export function isIpAddressHost() {
  if (typeof window === 'undefined') return false;
  return /^\d{1,3}(\.\d{1,3}){3}$/.test(window.location.hostname);
}

function webPushSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

function domainRequiredReason() {
  if (typeof window !== 'undefined' && !window.isSecureContext) return 'needs-domain';
  if (isIpAddressHost()) return 'needs-domain';
  return null;
}

export function hasWebPushSubscription() {
  return subscribed;
}

export function shouldShowNotificationButton() {
  if (isNativeApp()) return false;
  if (typeof window === 'undefined' || !window.isSecureContext) return false;
  if (!webPushSupported()) return false;
  if (typeof Notification !== 'undefined' && Notification.permission !== 'default') {
    return false;
  }
  return true;
}

export async function startWebPush(token, { interactive = false } = {}) {
  if (isNativeApp() || !token) return { ok: false, reason: 'native' };

  if (typeof window !== 'undefined' && !window.isSecureContext) {
    return { ok: false, reason: 'needs-domain' };
  }

  if (!webPushSupported()) {
    const domain = domainRequiredReason();
    if (domain) return { ok: false, reason: domain };
    if (isIosDevice() && !isStandalonePwa()) {
      return { ok: false, reason: 'add-to-home' };
    }
    return { ok: false, reason: 'unsupported' };
  }

  if (!interactive && Notification.permission === 'default') {
    return { ok: false, reason: 'needs-gesture' };
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    await navigator.serviceWorker.ready;

    let permission = Notification.permission;
    if (permission === 'default') {
      permission = await Notification.requestPermission();
    }
    if (permission !== 'granted') {
      if (isIosDevice() && !isStandalonePwa()) {
        return { ok: false, reason: 'add-to-home' };
      }
      return { ok: false, reason: 'denied' };
    }

    const vapidResponse = await axios.get(`${API_URL}/devices/vapid-public`);
    const publicKey = vapidResponse.data?.publicKey;
    if (!publicKey) return { ok: false, reason: 'vapid' };

    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      try {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey)
        });
      } catch {
        const stale = await registration.pushManager.getSubscription();
        if (stale) await stale.unsubscribe();
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey)
        });
      }
    }

    const payload = subscription.toJSON();
    lastEndpoint = payload.endpoint;
    await axios.post(`${API_URL}/devices/web-push`, payload, {
      headers: { Authorization: `Bearer ${token}` }
    });
    subscribed = true;
    return { ok: true };
  } catch (error) {
    const domain = domainRequiredReason();
    if (domain) return { ok: false, reason: domain, detail: error?.message };
    if (isIosDevice() && !isStandalonePwa()) {
      return { ok: false, reason: 'add-to-home' };
    }
    return { ok: false, reason: 'failed', detail: error?.message };
  }
}

export async function stopWebPush(authToken) {
  if (isNativeApp() || !webPushSupported()) return;
  try {
    const registration = await navigator.serviceWorker.getRegistration('/');
    const subscription = await registration?.pushManager?.getSubscription();
    const endpoint = subscription?.endpoint || lastEndpoint;
    if (authToken && endpoint) {
      await axios.delete(`${API_URL}/devices/web-push`, {
        headers: { Authorization: `Bearer ${authToken}` },
        data: { endpoint }
      });
    }
  } catch {
    // ignore
  }
  lastEndpoint = null;
  subscribed = false;
}
